import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

import { useAuth } from './lib/store';
import { connectSocket, getSocket, disconnectSocket } from './lib/socket';

import Shell from './components/Shell';
import ErrorBoundary from './components/ErrorBoundary';

import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import AuthSetup from './pages/AuthSetup';

import Feed from './pages/Feed';
import Groups from './pages/Groups';
import GroupPage from './pages/GroupPage';
import PostPage from './pages/PostPage';

import Shops from './pages/Shops';
import ShopPage from './pages/ShopPage';
import CreateShop from './pages/CreateShop';
import ShopDashboard from './pages/ShopDashboard';

import HeroPage from './pages/HeroPage';
import Inventory from './pages/Inventory';
import ItemShop from './pages/ItemShop';
import PetPage from './pages/PetPage';
import PetShop from './pages/PetShop';
import Garage from './pages/Garage';
import PartsShop from './pages/PartsShop';

import Arena from './pages/Arena';
import FightRoom from './pages/FightRoom';
import FightAccept from './pages/FightAccept';
import RaceHub from './pages/RaceHub';
import RaceRoom from './pages/RaceRoom';
import RaceAccept from './pages/RaceAccept';

import Leaderboard from './pages/Leaderboard';
import Guilds from './pages/Guilds';
import GuildPage from './pages/GuildPage';
import Profile from './pages/Profile';
import Wallet from './pages/Wallet';
import Referrals from './pages/Referrals';
import Notifications from './pages/Notifications';
import Chat from './pages/Chat';
import ChatRoom from './pages/ChatRoom';
import Trivia from './pages/Trivia';
import Settings from './pages/Settings';
import Admin from './pages/admin/Admin';

/* ============================================================
   QUERY CLIENT
   ============================================================ */
const qc = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => {
        if (err?.status === 429) return false;
        if (err?.status >= 400 && err?.status < 500) return false;
        return count < 2;
      },
      retryDelay: (i) => Math.min(1000 * 2 ** i, 8000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
    mutations: { retry: false },
  },
});

/* ============================================================
   HELPERS
   ============================================================ */
function EB({ name, children }) {
  return <ErrorBoundary name={name}>{children}</ErrorBoundary>;
}

function Protected({ children }) {
  const token = useAuth(s => s.token);
  const loading = useAuth(s => s.loading);

  if (!token) return <Navigate to="/login" replace />;

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <div className="text-center">
          <div
            className="w-12 h-12 mx-auto rounded-2xl grid place-items-center font-display font-black text-xl text-white animate-pulse"
            style={{ background: 'var(--frame, linear-gradient(135deg,#e8c874,#c9973a))' }}
          >
            W
          </div>
          <p className="text-sm mt-3 font-semibold" style={{ color: 'var(--text-muted, #97815f)' }}>
            Loading…
          </p>
        </div>
      </div>
    );
  }

  return children;
}

/* ============================================================
   APP
   ============================================================ */
export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Boot />

        <Toaster
          position="top-center"
          toastOptions={{
            duration: 3200,
            style: {
              background: 'var(--surface, #fbfaf7)',
              color: 'var(--text, #241e16)',
              border: '1px solid var(--border, #e8dcc8)',
              borderRadius: 'var(--radius, 1rem)',
              fontWeight: 600,
              fontSize: 14,
            },
          }}
        />

        <Routes>
          {/* ── PUBLIC ── */}
          <Route path="/" element={<EB name="Landing"><Landing /></EB>} />
          <Route path="/login" element={<EB name="Login"><Login /></EB>} />
          <Route path="/join" element={<EB name="Register"><Register /></EB>} />
          <Route path="/auth/callback" element={<EB name="AuthCallback"><AuthCallback /></EB>} />
          <Route path="/auth/setup" element={<EB name="AuthSetup"><AuthSetup /></EB>} />

          {/* ── APP SHELL ── */}
          <Route
            element={
              <Protected>
                <EB name="Shell"><Shell /></EB>
              </Protected>
            }
          >
            <Route path="/feed" element={<EB name="Feed"><Feed /></EB>} />
            <Route path="/groups" element={<EB name="Groups"><Groups /></EB>} />
            <Route path="/g/:handle" element={<EB name="GroupPage"><GroupPage /></EB>} />
            <Route path="/post/:id" element={<EB name="PostPage"><PostPage /></EB>} />
            <Route path="/p/:id" element={<EB name="PostPage"><PostPage /></EB>} />

            <Route path="/shops" element={<EB name="Shops"><Shops /></EB>} />
            <Route path="/shop/:slug" element={<EB name="ShopPage"><ShopPage /></EB>} />
            <Route path="/create-shop" element={<EB name="CreateShop"><CreateShop /></EB>} />
            <Route path="/shop/:id/dashboard" element={<EB name="ShopDashboard"><ShopDashboard /></EB>} />

            <Route path="/hero" element={<EB name="HeroPage"><HeroPage /></EB>} />
            <Route path="/inventory" element={<EB name="Inventory"><Inventory /></EB>} />
            <Route path="/item-shop" element={<EB name="ItemShop"><ItemShop /></EB>} />
            <Route path="/pet" element={<EB name="PetPage"><PetPage /></EB>} />
            <Route path="/pet-shop" element={<EB name="PetShop"><PetShop /></EB>} />
            <Route path="/garage" element={<EB name="Garage"><Garage /></EB>} />
            <Route path="/parts-shop" element={<EB name="PartsShop"><PartsShop /></EB>} />

            <Route path="/arena" element={<EB name="Arena"><Arena /></EB>} />
            <Route path="/fight/:id" element={<EB name="FightRoom"><FightRoom /></EB>} />
            <Route path="/fight/accept/:code" element={<EB name="FightAccept"><FightAccept /></EB>} />
            <Route path="/race" element={<EB name="RaceHub"><RaceHub /></EB>} />
            <Route path="/race/:id" element={<EB name="RaceRoom"><RaceRoom /></EB>} />
            <Route path="/race/accept/:code" element={<EB name="RaceAccept"><RaceAccept /></EB>} />

            <Route path="/leaderboard" element={<EB name="Leaderboard"><Leaderboard /></EB>} />
            <Route path="/guilds" element={<EB name="Guilds"><Guilds /></EB>} />
            <Route path="/guild/:handle" element={<EB name="GuildPage"><GuildPage /></EB>} />
            <Route path="/u/:username" element={<EB name="Profile"><Profile /></EB>} />
            <Route path="/wallet" element={<EB name="Wallet"><Wallet /></EB>} />
            <Route path="/referrals" element={<EB name="Referrals"><Referrals /></EB>} />
            <Route path="/notifications" element={<EB name="Notifications"><Notifications /></EB>} />
            <Route path="/chat" element={<EB name="Chat"><Chat /></EB>} />
            <Route path="/chat/:roomType/:roomId" element={<EB name="ChatRoom"><ChatRoom /></EB>} />
            <Route path="/trivia" element={<EB name="Trivia"><Trivia /></EB>} />
            <Route path="/settings" element={<EB name="Settings"><Settings /></EB>} />
            <Route path="/admin/*" element={<EB name="Admin"><Admin /></EB>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

/* ============================================================
   BOOT — loads /auth/me once, wires sockets
   ============================================================ */
function Boot() {
  const token = useAuth(s => s.token);
  const load = useAuth(s => s.load);

  /* ---- initial user load, once per token ---- */
  useEffect(() => {
    if (token) {
      load(true);
    } else {
      useAuth.setState({ loading: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ---- sockets, once per token ---- */
  useEffect(() => {
    if (!token) {
      disconnectSocket();
      return;
    }

    connectSocket(token);
    const s = getSocket();
    if (!s) return;

    const onPoints = (d) => {
      toast.success(`💎 +${d.points} points${d.source ? ` from ${d.source}` : ''}`);
      useAuth.getState().addPoints(d.points);
    };

    const onNotif = () => {
      useAuth.setState(st => ({ unread: (st.unread || 0) + 1 }));
    };

    const onGift = (d) => {
      toast(`🎁 ${d.from} sent you a ${d.rarity} ${d.item}!`, { duration: 7000 });
      useAuth.setState(st => ({ pendingGifts: (st.pendingGifts || 0) + 1 }));
    };

    const onDrop = (d) => {
      toast.success(`${d.icon || '🎁'} ${d.rarityLabel || ''} ${d.name} dropped!`, { duration: 6000 });
    };

    const onShopStatus = (d) => {
      toast(d.status === 'approved' ? '🏪 Your shop was approved!' : `Shop ${d.status}`);
      qc.invalidateQueries({ queryKey: ['shops'] });
    };

    const onAccount = (d) => {
      toast.error(`Your account was ${d.status}`);
      setTimeout(() => useAuth.getState().logout(), 1500);
    };

    s.on('points:earned', onPoints);
    s.on('notification:new', onNotif);
    s.on('gift:received', onGift);
    s.on('item:drop', onDrop);
    s.on('shop:status', onShopStatus);
    s.on('account:status', onAccount);

    return () => {
      s.off('points:earned', onPoints);
      s.off('notification:new', onNotif);
      s.off('gift:received', onGift);
      s.off('item:drop', onDrop);
      s.off('shop:status', onShopStatus);
      s.off('account:status', onAccount);
    };
  }, [token]);

  return null;
}